import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaLock, FaLockOpen, FaSpinner } from "react-icons/fa";
import { useAuth } from "../../Authentication/AuthContext";
import Login from "../../Authentication/Login/Login";
import SubscriptionMonthly from "../../Authentication/Subscription/SubscriptionMonthly";
import "./BooksPageStyles.css";

type BookLevelKey = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

type ProtectedBook = {
  level: BookLevelKey;
  book_id: number;
  title: string;
  file_name: string;
  api_path: string;
  view_url: string;
  size_bytes?: number;
  updated_at?: string;
};

type ProtectedBookLevel = {
  level: BookLevelKey;
  title: string;
  subtitle: string;
  book_count: number;
  books: ProtectedBook[];
};

type ProtectedBooksResponse = {
  email: string;
  has_record: boolean;

  has_full_access: boolean;
  has_full_course_access?: boolean;
  has_full_book_access?: boolean;

  course_access_levels: string[];
  book_access_levels?: string[];

  accessible_levels: BookLevelKey[];
  accessible_course_levels?: BookLevelKey[];
  accessible_book_levels?: BookLevelKey[];

  total_books: number;
  levels: ProtectedBookLevel[];
};

type CachedProtectedBooksResponse = {
  saved_at: number;
  data: ProtectedBooksResponse;
};

type DisplayBook = ProtectedBook & {
  is_unlocked: boolean;
};

type DisplayBookLevel = {
  level: BookLevelKey;
  title: string;
  subtitle: string;
  book_count: number;
  unlocked_count: number;
  books: DisplayBook[];
};

type BooksAccessStatus = "idle" | "checking" | "ready" | "error";

const API_BASE_URL = "https://www.api.dearlanguage.com";
const BOOKS_CACHE_PREFIX = "dear-language-protected-books-v4";
const OLD_BOOKS_CACHE_PREFIXES = [
  "dear-language-protected-books",
  "dear-language-protected-books-v1",
  "dear-language-protected-books-v2",
  "dear-language-protected-books-v3",
];
const BOOKS_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 12;

const ALL_BOOK_LEVELS: BookLevelKey[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

const LEVEL_META: Record<BookLevelKey, { title: string; subtitle: string }> = {
  A1: {
    title: "Anfänger",
    subtitle: "",
  },
  A2: {
    title: "Grundlagen",
    subtitle: "",
  },
  B1: {
    title: "Mittelstufe",
    subtitle: "",
  },
  B2: {
    title: "Oberstufe",
    subtitle: "",
  },
  C1: {
    title: "Profi",
    subtitle: "",
  },
  C2: {
    title: "Experte",
    subtitle: "",
  },
};

const isBookLevel = (value: unknown): value is BookLevelKey => {
  const cleanValue = String(value || "")
    .trim()
    .toUpperCase();

  return (
    cleanValue === "A1" ||
    cleanValue === "A2" ||
    cleanValue === "B1" ||
    cleanValue === "B2" ||
    cleanValue === "C1" ||
    cleanValue === "C2"
  );
};

const normalizeBookLevel = (value: unknown): BookLevelKey | null => {
  const cleanValue = String(value || "")
    .trim()
    .toUpperCase();

  return isBookLevel(cleanValue) ? cleanValue : null;
};

const sortBookLevels = (levels: ProtectedBookLevel[]): ProtectedBookLevel[] => {
  const order: Record<BookLevelKey, number> = {
    A1: 1,
    A2: 2,
    B1: 3,
    B2: 4,
    C1: 5,
    C2: 6,
  };

  return [...levels].sort((a, b) => order[a.level] - order[b.level]);
};

const sortBooks = (books: ProtectedBook[]): ProtectedBook[] => {
  return [...books].sort((a, b) => a.book_id - b.book_id);
};

const createPublicBook = (
  level: BookLevelKey,
  bookId: number,
): ProtectedBook => {
  return {
    level,
    book_id: bookId,
    title: `Deutsch ${level}.${bookId}`,
    file_name: `${level}_${bookId}.html`,
    api_path: `/api/protected-books/${level}/${bookId}`,
    view_url: "",
  };
};

const createPublicBookLevels = (): ProtectedBookLevel[] => {
  return ALL_BOOK_LEVELS.map((level) => {
    const meta = LEVEL_META[level];

    const books = Array.from({ length: 10 }, (_, index) =>
      createPublicBook(level, index + 1),
    );

    return {
      level,
      title: meta.title,
      subtitle: meta.subtitle,
      book_count: books.length,
      books,
    };
  });
};

const PUBLIC_BOOK_LEVELS = createPublicBookLevels();

const getBooksCacheKey = (email: string): string => {
  return `${BOOKS_CACHE_PREFIX}:${email.trim().toLowerCase()}`;
};

const clearOldBookCaches = (email?: string | null): void => {
  const cleanEmail = email?.trim().toLowerCase() || "";

  if (!cleanEmail) {
    return;
  }

  try {
    OLD_BOOKS_CACHE_PREFIXES.forEach((prefix) => {
      window.sessionStorage.removeItem(`${prefix}:${cleanEmail}`);
    });
  } catch {
    // Cache löschen ist optional.
  }
};

const normalizeProtectedBooksResponse = (
  raw: Partial<ProtectedBooksResponse> | null | undefined,
): ProtectedBooksResponse => {
  const safeRaw: Partial<ProtectedBooksResponse> = raw ?? {};
  const safeLevels = Array.isArray(safeRaw.levels) ? safeRaw.levels : [];

  const levels: ProtectedBookLevel[] = safeLevels
    .map((levelGroup): ProtectedBookLevel | null => {
      const normalizedLevel = normalizeBookLevel(levelGroup?.level);

      if (!normalizedLevel) {
        return null;
      }

      const books = Array.isArray(levelGroup?.books)
        ? levelGroup.books
            .map((book): ProtectedBook | null => {
              const bookLevel = normalizeBookLevel(book?.level);

              if (!bookLevel) {
                return null;
              }

              const bookId = Number(book?.book_id || 0);

              if (!Number.isInteger(bookId) || bookId < 1 || bookId > 10) {
                return null;
              }

              return {
                level: bookLevel,
                book_id: bookId,
                title: String(book?.title || `Deutsch ${bookLevel}.${bookId}`),
                file_name: String(
                  book?.file_name || `${bookLevel}_${bookId}.html`,
                ),
                api_path: String(
                  book?.api_path ||
                    `/api/protected-books/${bookLevel}/${bookId}`,
                ),
                view_url: String(book?.view_url || ""),
                size_bytes: Number(book?.size_bytes || 0),
                updated_at: book?.updated_at ? String(book.updated_at) : "",
              };
            })
            .filter((book): book is ProtectedBook => Boolean(book))
        : [];

      return {
        level: normalizedLevel,
        title: String(levelGroup?.title || LEVEL_META[normalizedLevel].title),
        subtitle: "",
        book_count: Number(levelGroup?.book_count || books.length),
        books: sortBooks(books),
      };
    })
    .filter((level): level is ProtectedBookLevel => Boolean(level));

  const sortedLevels = sortBookLevels(levels);

  const accessibleBookLevelsSource = Array.isArray(
    safeRaw.accessible_book_levels,
  )
    ? safeRaw.accessible_book_levels
    : safeRaw.accessible_levels;

  const accessibleBookLevels = Array.isArray(accessibleBookLevelsSource)
    ? accessibleBookLevelsSource
        .map((item) => normalizeBookLevel(item))
        .filter((item): item is BookLevelKey => Boolean(item))
    : sortedLevels.map((levelGroup) => levelGroup.level);

  const accessibleCourseLevels = Array.isArray(safeRaw.accessible_course_levels)
    ? safeRaw.accessible_course_levels
        .map((item) => normalizeBookLevel(item))
        .filter((item): item is BookLevelKey => Boolean(item))
    : [];

  const totalBooks =
    Number(safeRaw.total_books || 0) ||
    sortedLevels.reduce(
      (total, levelGroup) => total + levelGroup.books.length,
      0,
    );

  return {
    email: String(safeRaw.email || ""),
    has_record: Boolean(safeRaw.has_record),

    has_full_access: Boolean(
      safeRaw.has_full_book_access ?? safeRaw.has_full_access,
    ),
    has_full_course_access: Boolean(safeRaw.has_full_course_access),
    has_full_book_access: Boolean(
      safeRaw.has_full_book_access ?? safeRaw.has_full_access,
    ),

    course_access_levels: Array.isArray(safeRaw.course_access_levels)
      ? safeRaw.course_access_levels.map((item) => String(item))
      : [],

    book_access_levels: Array.isArray(safeRaw.book_access_levels)
      ? safeRaw.book_access_levels.map((item) => String(item))
      : [],

    accessible_levels: accessibleBookLevels,
    accessible_course_levels: accessibleCourseLevels,
    accessible_book_levels: accessibleBookLevels,

    total_books: totalBooks,
    levels: sortedLevels,
  };
};

const getCachedBooksData = (email: string): ProtectedBooksResponse | null => {
  try {
    const cacheKey = getBooksCacheKey(email);
    const cachedValue = window.sessionStorage.getItem(cacheKey);

    if (!cachedValue) {
      return null;
    }

    const parsed = JSON.parse(cachedValue) as CachedProtectedBooksResponse;

    if (!parsed?.data || !parsed?.saved_at) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    if (Date.now() - parsed.saved_at > BOOKS_CACHE_MAX_AGE_MS) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    return normalizeProtectedBooksResponse(parsed.data);
  } catch {
    return null;
  }
};

const saveBooksDataToCache = (
  email: string,
  data: ProtectedBooksResponse,
): void => {
  try {
    const cacheKey = getBooksCacheKey(email);

    const cachedValue: CachedProtectedBooksResponse = {
      saved_at: Date.now(),
      data,
    };

    window.sessionStorage.setItem(cacheKey, JSON.stringify(cachedValue));
  } catch {
    // Cache ist optional.
  }
};

const removeBooksDataFromCache = (email?: string | null): void => {
  const cleanEmail = email?.trim().toLowerCase() || "";

  if (!cleanEmail) {
    return;
  }

  try {
    window.sessionStorage.removeItem(getBooksCacheKey(cleanEmail));
    clearOldBookCaches(cleanEmail);
  } catch {
    // Cache löschen ist optional.
  }
};

const fetchProtectedBooks = async (
  email: string,
): Promise<ProtectedBooksResponse> => {
  const cleanEmail = email.trim().toLowerCase();

  const response = await fetch(
    `${API_BASE_URL}/api/protected-books?email=${encodeURIComponent(cleanEmail)}`,
    {
      method: "GET",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Der Bücher-Zugang konnte nicht geprüft werden. Status: ${response.status}`,
    );
  }

  const json = (await response.json()) as ProtectedBooksResponse;
  const normalized = normalizeProtectedBooksResponse(json);

  saveBooksDataToCache(cleanEmail, normalized);

  return normalized;
};

const mergeBooksWithAccess = (
  booksData: ProtectedBooksResponse | null,
): DisplayBookLevel[] => {
  const unlockedBooksMap = new Map<string, ProtectedBook>();
  const unlockedLevels = new Set<BookLevelKey>();

  if (booksData?.has_full_book_access || booksData?.has_full_access) {
    ALL_BOOK_LEVELS.forEach((level) => unlockedLevels.add(level));
  }

  const accessibleLevels =
    booksData?.accessible_book_levels || booksData?.accessible_levels || [];

  accessibleLevels.forEach((level) => {
    if (isBookLevel(level)) {
      unlockedLevels.add(level);
    }
  });

  booksData?.levels?.forEach((levelGroup) => {
    levelGroup.books.forEach((book) => {
      unlockedBooksMap.set(`${book.level}-${book.book_id}`, book);
    });
  });

  return PUBLIC_BOOK_LEVELS.map((levelGroup) => {
    const books: DisplayBook[] = levelGroup.books.map((publicBook) => {
      const unlockedBook =
        unlockedBooksMap.get(`${publicBook.level}-${publicBook.book_id}`) ||
        null;

      const isUnlocked =
        Boolean(
          booksData?.has_full_book_access || booksData?.has_full_access,
        ) ||
        unlockedLevels.has(publicBook.level) ||
        Boolean(unlockedBook);

      return {
        ...publicBook,
        ...(unlockedBook || {}),
        is_unlocked: isUnlocked,
      };
    });

    return {
      level: levelGroup.level,
      title: levelGroup.title,
      subtitle: levelGroup.subtitle,
      book_count: books.length,
      unlocked_count: books.filter((book) => book.is_unlocked).length,
      books,
    };
  });
};

const buildBookPath = (book: DisplayBook): string => {
  return `/book-view?level=${encodeURIComponent(
    book.level,
  )}&bookId=${encodeURIComponent(
    String(book.book_id),
  )}&book=${encodeURIComponent(book.title)}&file=${encodeURIComponent(
    book.file_name,
  )}`;
};

const BooksPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, authLoading, userEmail } = useAuth();

  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showSubscriptionMonthly, setShowSubscriptionMonthly] =
    useState<boolean>(false);
  const [pendingBook, setPendingBook] = useState<DisplayBook | null>(null);
  const [openPendingBookAfterLogin, setOpenPendingBookAfterLogin] =
    useState<boolean>(false);
  const [accessStatus, setAccessStatus] = useState<BooksAccessStatus>("idle");
  const [accessError, setAccessError] = useState<string>("");
  const [booksData, setBooksData] = useState<ProtectedBooksResponse | null>(
    null,
  );
  const [retryKey, setRetryKey] = useState<number>(0);

  useEffect(() => {
    let isCancelled = false;

    const loadBooks = async (): Promise<void> => {
      setAccessError("");

      if (authLoading) {
        setAccessStatus("checking");
        return;
      }

      if (!isLoggedIn) {
        setBooksData(null);
        setAccessStatus("ready");
        return;
      }

      const cleanEmail = userEmail?.trim().toLowerCase() || "";

      if (!cleanEmail) {
        setBooksData(null);
        setAccessStatus("error");
        setAccessError(
          "Ihre E-Mail-Adresse konnte nicht gelesen werden. Bitte loggen Sie sich erneut ein.",
        );
        return;
      }

      clearOldBookCaches(cleanEmail);

      const cachedData = getCachedBooksData(cleanEmail);

      if (cachedData) {
        setBooksData(cachedData);
        setAccessStatus("ready");
        return;
      }

      setBooksData(null);
      setAccessStatus("checking");

      try {
        const protectedBooks = await fetchProtectedBooks(cleanEmail);

        if (!isCancelled) {
          setBooksData(protectedBooks);
          setAccessStatus("ready");
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Der Bücher-Zugang konnte nicht geprüft werden.";

        if (!isCancelled) {
          setAccessStatus("error");
          setAccessError(message);
        }
      }
    };

    void loadBooks();

    return () => {
      isCancelled = true;
    };
  }, [authLoading, isLoggedIn, userEmail, retryKey]);

  const visibleBookLevels = useMemo(() => {
    return mergeBooksWithAccess(booksData);
  }, [booksData]);

  const totalVisibleBooks = useMemo(() => {
    return visibleBookLevels.reduce(
      (total, bookLevel) => total + bookLevel.books.length,
      0,
    );
  }, [visibleBookLevels]);

  const totalUnlockedBooks = useMemo(() => {
    return visibleBookLevels.reduce(
      (total, bookLevel) => total + bookLevel.unlocked_count,
      0,
    );
  }, [visibleBookLevels]);

  const hasFullBookAccess = Boolean(
    booksData?.has_full_book_access || booksData?.has_full_access,
  );

  const isCheckingAccess =
    authLoading || (isLoggedIn && accessStatus === "checking");

  useEffect(() => {
    if (
      authLoading ||
      !isLoggedIn ||
      !pendingBook ||
      !openPendingBookAfterLogin ||
      showLoginModal
    ) {
      return;
    }

    if (accessStatus === "idle" || accessStatus === "checking") {
      return;
    }

    const latestPendingBook = visibleBookLevels
      .flatMap((bookLevel) => bookLevel.books)
      .find(
        (book) =>
          book.level === pendingBook.level &&
          book.book_id === pendingBook.book_id,
      );

    if (latestPendingBook?.is_unlocked) {
      setPendingBook(null);
      setOpenPendingBookAfterLogin(false);
      navigate(buildBookPath(latestPendingBook));
      return;
    }

    setPendingBook(null);
    setOpenPendingBookAfterLogin(false);
  }, [
    accessStatus,
    authLoading,
    isLoggedIn,
    navigate,
    openPendingBookAfterLogin,
    pendingBook,
    showLoginModal,
    visibleBookLevels,
  ]);

  const headerTitle = hasFullBookAccess
    ? "Alle Deutschbücher"
    : isLoggedIn
      ? "Meine Deutschbücher"
      : "Deutschbücher";

  const headerSubtitle = !isLoggedIn
    ? "Alle Buchtitel sind sichtbar. Zum Öffnen bitte einloggen und den Premium-Zugang abonnieren."
    : hasFullBookAccess
      ? "Vollzugang: Alle Bücher von A1 bis C2 sind freigeschaltet."
      : "Freigeschaltete Bücher können geöffnet werden. Gesperrte Bücher benötigen Kurszugang oder Premium-Zugang.";

  const handleRefreshAccess = (): void => {
    removeBooksDataFromCache(userEmail);
    setRetryKey((current) => current + 1);
  };

  const handleLoginClose = (): void => {
    setShowLoginModal(false);
  };

  const openLoginForBook = (book: DisplayBook): void => {
    setPendingBook(book);
    setShowSubscriptionMonthly(false);
    setOpenPendingBookAfterLogin(true);
    setShowLoginModal(true);
  };

  const openSubscriptionMonthly = (book?: DisplayBook): void => {
    if (book) {
      setPendingBook(book);
    }

    setShowLoginModal(false);
    setOpenPendingBookAfterLogin(false);
    setShowSubscriptionMonthly(true);
  };

  const handleSubscribed = (): void => {
    setShowSubscriptionMonthly(false);
    setPendingBook(null);
    setOpenPendingBookAfterLogin(false);
    removeBooksDataFromCache(userEmail);
    setRetryKey((current) => current + 1);
  };

  const openBook = (book: DisplayBook): void => {
    if (isCheckingAccess) {
      return;
    }

    if (!isLoggedIn) {
      openLoginForBook(book);
      return;
    }

    if (!book.is_unlocked) {
      openSubscriptionMonthly(book);
      return;
    }

    navigate(buildBookPath(book));
  };

  const renderCardParticles = (): React.ReactNode => {
    return (
      <div className="book-card-particles" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
    );
  };

  const renderStatusNotice = (): React.ReactNode => {
    if (authLoading || accessStatus === "checking") {
      return null;
    }

    if (accessStatus !== "error") {
      return null;
    }

    return (
      <article className="book-group book-status-group">
        {renderCardParticles()}

        <div className="book-card-badge book-card-badge-status">
          <section className="book-card-left">
            <div className="book-lock-block">
              <span className="book-level-icon book-level-icon-locked">
                <FaLock />
              </span>

              <span className="book-lock-status">Fehler</span>
            </div>

            <div className="book-title-block">
              <h2>!</h2>
              <p>Zugang</p>
            </div>
          </section>

          <section className="book-card-right book-card-right-message">
            <button
              type="button"
              className="book-button book-message-button"
              onClick={handleRefreshAccess}
            >
              <span className="book-name">
                {accessError || "Zugang erneut prüfen"}
              </span>

              <span className="book-arrow book-arrow-locked">→</span>
            </button>
          </section>
        </div>
      </article>
    );
  };

  return (
    <div className="books-page">
      <Login isOpen={showLoginModal} onClose={handleLoginClose} />

      <SubscriptionMonthly
        isOpen={showSubscriptionMonthly}
        onClose={() => {
          setShowSubscriptionMonthly(false);
          setPendingBook(null);
          setOpenPendingBookAfterLogin(false);
        }}
        onSubscribed={handleSubscribed}
      />

      <section className="books-container">
        <header className="books-header-card">
          <div className="books-header-content">
            <p className="books-kicker">Deutschbibliothek</p>
            <h1 className="books-page-title">{headerTitle}</h1>
            <p className="books-subtitle">{headerSubtitle}</p>
          </div>

          <div className="books-header-badge">
            <strong>
              {isCheckingAccess
                ? "..."
                : `${totalUnlockedBooks}/${totalVisibleBooks}`}
            </strong>
            <span>Freigeschaltete Bücher</span>
          </div>
        </header>

        {renderStatusNotice()}

        <div className="books-learning-grid">
          {visibleBookLevels.map(({ level, title, books, unlocked_count }) => {
            const isLevelUnlocked = unlocked_count > 0;

            const cardStatusClassName = isCheckingAccess
              ? "book-card-checking"
              : isLevelUnlocked
                ? "book-card-unlocked"
                : "book-card-locked";

            const levelIconClassName = isCheckingAccess
              ? "book-level-icon-checking"
              : isLevelUnlocked
                ? "book-level-icon-unlocked"
                : "book-level-icon-locked";

            const statusText = isCheckingAccess
              ? "Prüfen"
              : isLevelUnlocked
                ? "Freigeschaltet"
                : "Gesperrt";

            const countText = isCheckingAccess
              ? "Prüfung läuft"
              : `${unlocked_count}/${books.length} freigeschaltet`;

            return (
              <article
                key={level}
                className={`book-group book-${level.toLowerCase()} ${cardStatusClassName}`}
              >
                {renderCardParticles()}

                <div className="book-card-badge">
                  <section className="book-card-left">
                    <div className="book-lock-block">
                      <span className={`book-level-icon ${levelIconClassName}`}>
                        {isCheckingAccess ? (
                          <FaSpinner className="book-checking-icon" />
                        ) : isLevelUnlocked ? (
                          <FaLockOpen />
                        ) : (
                          <FaLock />
                        )}
                      </span>

                      <span className="book-lock-status">{statusText}</span>
                    </div>

                    <div className="book-title-block">
                      <h2>{level}</h2>
                      <p>{title}</p>
                      <small>{countText}</small>
                    </div>
                  </section>

                  <section className="book-card-right">
                    <ul className="book-list">
                      {books.map((book) => {
                        const arrowClassName = isCheckingAccess
                          ? "book-arrow-checking"
                          : book.is_unlocked
                            ? "book-arrow-unlocked"
                            : "book-arrow-locked";

                        return (
                          <li
                            key={`${book.level}-${book.book_id}`}
                            className="book-item"
                          >
                            <button
                              type="button"
                              className={`book-button ${
                                book.is_unlocked ? "" : "book-button-locked"
                              }`}
                              onClick={() => openBook(book)}
                              aria-label={`${book.title} öffnen`}
                            >
                              <span className="book-name">{book.title}</span>

                              <span className={`book-arrow ${arrowClassName}`}>
                                {isCheckingAccess ? (
                                  <FaSpinner className="book-checking-icon" />
                                ) : book.is_unlocked ? (
                                  <FaLockOpen />
                                ) : (
                                  <FaLock />
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default BooksPage;